import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { restoreSession, logout, type AuthUser } from './auth';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import LeadHRDashboard from './Dashboard';
import Login from './Login';
import './theme.css';

type Session = { status: 'checking' } | { status: 'out' } | { status: 'in'; user: AuthUser };

type View =
  | { name: 'login' }
  | { name: 'forgot' }
  | { name: 'reset'; userId: string; token: string };

/**
 * Reads the signed-out view from the address bar.
 *
 * The reset link arrives by email as `/reset-password?token=…&userId=…`, so the path has
 * to select the screen. Three flat screens do not justify a router — but they do need
 * real URLs, since one of them is only ever reached from an email.
 */
function readView(): View {
  const { pathname, searchParams } = new URL(window.location.href);
  if (pathname.startsWith('/reset-password')) {
    return {
      name: 'reset',
      userId: searchParams.get('userId') ?? '',
      token: searchParams.get('token') ?? '',
    };
  }
  if (pathname.startsWith('/forgot-password')) {
    return { name: 'forgot' };
  }
  return { name: 'login' };
}

/**
 * The dashboard is its own application: a Lead HR signs in here and never needs the chat
 * app. Sessions are restored from the refresh cookie on load, so a reload does not drop
 * them back to the sign-in screen.
 */
export default function App(): ReactElement {
  const [session, setSession] = useState<Session>({ status: 'checking' });
  const [view, setView] = useState<View>(readView);

  useEffect(() => {
    let cancelled = false;
    void restoreSession().then((user) => {
      if (!cancelled) {
        setSession(user ? { status: 'in', user } : { status: 'out' });
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Back and forward must move between the signed-out screens, not leave the app. */
  useEffect(() => {
    const onPopState = () => setView(readView());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const go = useCallback((path: string, next: View) => {
    window.history.pushState(null, '', path);
    setView(next);
  }, []);

  if (session.status === 'checking') {
    /** Blank rather than a spinner: the check is a single request, and loading chrome on
     *  every load is worse than a beat of nothing. */
    return <div className="leadhr min-h-screen" />;
  }

  if (session.status === 'in') {
    return (
      <LeadHRDashboard
        userName={session.user.name ?? session.user.email}
        onSignOut={() => {
          void logout().then(() => {
            go('/', { name: 'login' });
            setSession({ status: 'out' });
          });
        }}
      />
    );
  }

  if (view.name === 'reset') {
    return (
      <ResetPassword
        userId={view.userId}
        token={view.token}
        onDone={() => go('/', { name: 'login' })}
      />
    );
  }

  if (view.name === 'forgot') {
    return <ForgotPassword onBack={() => go('/', { name: 'login' })} />;
  }

  return (
    <Login
      onSignedIn={(user) => setSession({ status: 'in', user })}
      onForgotPassword={() => go('/forgot-password', { name: 'forgot' })}
    />
  );
}
